import { isPlatformBrowser } from '@angular/common';
import { Component, computed, inject, OnDestroy, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Observable, map, Subscription } from 'rxjs';
import { TipoPergunta } from '../../core/enum/tipo-pergunta.enum';
import { QuestionarioStatus } from '../../core/enum/questionario-status.enum';
import { TipoQuestionario } from '../../core/enum/tipo-questionario.enum';
import {
  createQuestionarioFormDefault,
  QuestionarioAlternativa,
  QuestionarioFormValue,
  QuestionarioPergunta,
  QuestionarioPerfil,
} from '../../core/models/questionario-form.model';
import {
  QUESTIONARIO_STATUS_PUBLICADO,
  QUESTIONARIO_STATUS_RASCUNHO,
  StatusQuestionarioApi,
} from '../../core/api/models/questionario-status-api';
import { Questionario } from '../../core/models/questionario.model';
import { QuestionarioService } from '../../core/services/questionario.service';
import { QuestionarioSaveError } from '../../core/models/questionario-save-error.model';
import { PerfilService } from '../../core/services/perfil.service';
import { Perfil } from '../../core/models/perfil.model';
import {
  ProblemaCobertura,
  calcularPontuacaoAtingivel,
  validarCoberturaDePerfis,
} from '../../core/utils/questionario-pontuacao.util';
import { ApiHttpError } from '../../shared/services/http-client/api-http-error.model';
import { BackButtonComponent } from '../../shared/components/back-button/back-button.component';
import { AlertDialogComponent } from '../../shared/components/alert-dialog/alert-dialog.component';
import { ToastService } from '../../core/services/toast.service';
import { CriarPerguntaModalComponent } from './criar-pergunta-modal.component';
import { PerguntaModalComponent } from './pergunta-modal.component';
import {
  createQuestionarioPdfBlob,
  createQuestionarioPdfFileName,
} from './questionario-pdf';
import { SelecionarPerguntaModalComponent } from './selecionar-pergunta-modal.component';
import { VisualizarQuestionarioModalComponent } from './visualizar-questionario-modal.component';
import { PerfilAutocompleteComponent } from './perfil-autocomplete.component';
// Removido por decisão do PO — peso removido, ver plano
// 2026-08-11-questionario-erro-exclusao-e-remove-peso.
// import { pesoCoerenteValidator } from './peso-coerente.validator';
// import { pontosNaoExcedePesoValidator } from './pontos-alternativa.validator';

const MOTIVO_BLOQUEIO_EDICAO_PADRAO = 'Este questionário não pode ser editado.';

@Component({
  selector: 'app-questionario-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    BackButtonComponent,
    AlertDialogComponent,
    CriarPerguntaModalComponent,
    PerguntaModalComponent,
    SelecionarPerguntaModalComponent,
    VisualizarQuestionarioModalComponent,
    PerfilAutocompleteComponent,
  ],
  templateUrl: './questionario-form.component.html',
  styleUrl: './questionario-form.component.scss',
})
export class QuestionarioFormComponent implements OnInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly questionarioService = inject(QuestionarioService);
  private readonly perfilService = inject(PerfilService);
  private readonly toastService = inject(ToastService);
  private readonly platformId = inject(PLATFORM_ID);

  // Rastreia a subscription de alternativas.valueChanges por FormGroup de pergunta,
  // para cancelar tanto na remoção individual quanto no destroy do componente.
  private readonly perguntaAlternativasSubscriptions = new Map<FormGroup, Subscription>();

  private readonly questionarioId = toSignal(
    this.route.paramMap.pipe(
      map((params) => {
        const raw = params.get('id');
        return raw != null ? Number(raw) : null;
      }),
    ),
    { initialValue: null as number | null },
  );

  readonly isEditMode = computed(() => this.questionarioId() != null);
  readonly pageTitle = computed(() =>
    this.isEditMode() ? 'Editar questionário' : 'Criar questionário',
  );

  readonly form: FormGroup = this.fb.group({
    nome: ['', Validators.required],
    tipo: [TipoQuestionario.Suitability, Validators.required],
    perguntas: this.fb.array([]),
    perfis: this.fb.array([]),
  });

  /**
   * Contador incrementado a cada mudança do form; é a dependência que faz os `computed` de
   * validação recalcularem. Precisa ser um contador e não o valor emitido: o status volta a
   * repetir o mesmo texto ("VALID") entre emissões, e o sinal ignora um `set` com valor igual
   * ao anterior — os `computed` ficavam presos no resultado antigo.
   */
  private readonly formRevisao = signal(0);
  private readonly formRevisaoSubscription = this.form.valueChanges
    .pipe(takeUntilDestroyed())
    .subscribe(() => this.formRevisao.update((revisao) => revisao + 1));

  readonly perfilSelecionadoId = this.fb.control<number | null>(null);

  private readonly _perfisDisponiveis = signal<Perfil[]>([]);
  readonly perfisDisponiveis = this._perfisDisponiveis.asReadonly();

  readonly tiposQuestionario = Object.values(TipoQuestionario);

  readonly submitAttempted = signal(false);
  readonly isSaving = signal(false);
  readonly carregandoPerfis = signal(false);

  /** Status persistido do questionário em edição; `null` enquanto não carregou ou na criação. */
  private readonly statusCarregado = signal<QuestionarioStatus | null>(null);

  /**
   * Um questionário publicado não volta a ser rascunho, então "Salvar como rascunho" some da
   * tela em vez de aparecer desabilitado — a ação deixou de existir para ele.
   */
  readonly podeSalvarComoRascunho = computed(
    () => this.statusCarregado() !== QuestionarioStatus.Publicado,
  );

  /** Preenchidos a partir de `permiteEdicao`/`motivoBloqueioEdicao` da consulta do questionário. */
  readonly edicaoBloqueada = signal(false);
  readonly motivoBloqueioEdicao = signal('');

  /** Índice/modo da pergunta selecionada nos ícones Visualizar/Editar. */
  readonly perguntaSelecionada = signal<{ index: number; modo: 'visualizar' | 'editar' } | null>(
    null,
  );
  private readonly perguntaArrastada = signal<number | null>(null);

  readonly perguntaParaExcluir = signal<number | null>(null);
  readonly perfilParaExcluir = signal<number | null>(null);
  /** Alternativa aguardando confirmação; guarda o grupo da pergunta que a contém. */
  readonly alternativaParaExcluir = signal<{ perguntaGroup: FormGroup; index: number } | null>(
    null,
  );

  /** Bloqueia as lixeiras enquanto um DELETE está em andamento, para não repetir a chamada. */
  readonly excluindo = signal(false);

  /** Nome exibido na confirmação de exclusão do perfil associado. */
  readonly perfilParaExcluirNome = computed(() => {
    const index = this.perfilParaExcluir();
    if (index == null) {
      return '';
    }
    return (this.perfis.at(index)?.get('nomePerfil')?.value as string) ?? '';
  });

  /**
   * Perfis cuja pontuação já existe na API. A lixeira só dispara o DELETE para esses: um
   * perfil associado e ainda não salvo existe apenas no formulário, e chamar a API por ele
   * devolveria um 404 indistinguível de uma falha real.
   */
  private readonly pontuacoesPersistidas = new Set<number>();

  readonly showCriarPerguntaModal = signal(false);
  readonly criarPerguntaSubmitAttempted = signal(false);
  readonly criarPerguntaForm = this.fb.group({
    texto: ['', Validators.required],
  });

  readonly showSelecionarPerguntaModal = signal(false);
  readonly carregandoPerguntasCadastradas = signal(false);
  readonly perguntasCadastradas = signal<QuestionarioPergunta[]>([]);

  readonly showVisualizarModal = signal(false);
  readonly visualizarQuestionarioValue = signal<QuestionarioFormValue>(
    createQuestionarioFormDefault(),
  );
  
  get perguntas(): FormArray {
    return this.form.get('perguntas') as FormArray;
  }

  get perfis(): FormArray {
    return this.form.get('perfis') as FormArray;
  }

  /** Perfis ainda não associados a nenhuma linha — evita duplicar um perfil já adicionado. */
  get perfisDisponiveisParaSelecao(): Perfil[] {
    const idsAssociados = new Set(
      this.perfis.controls.map((grupo) => grupo.get('idPerfilInvestimento')?.value),
    );
    return this.perfisDisponiveis().filter((perfil) => !idsAssociados.has(perfil.id));
  }

  /**
   * Cadastro do perfil associado, para mostrar ao administrador o nome interno ao lado do nome
   * que o participante vê no portal. Fica `undefined` se o perfil não estiver entre os ativos.
   */
  perfilCadastrado(perfilGroup: FormGroup): Perfil | undefined {
    const id = perfilGroup.get('idPerfilInvestimento')?.value as number;
    return this.perfisDisponiveis().find((perfil) => perfil.id === id);
  }

  get perguntaGroups(): FormGroup[] {
    return this.perguntas.controls as FormGroup[];
  }

  get perfilGroups(): FormGroup[] {
    return this.perfis.controls as FormGroup[];
  }

  /**
   * Índices das perguntas que ainda bloqueiam o salvamento — alternativas de menos ou
   * texto/pontos em branco. Sem isso a pessoa só descobria o problema abrindo cada modal.
   *
   * É um `computed` e não uma leitura
   * direta de `grupo.invalid` no template porque a validade das perguntas é atualizada por uma
   * subscription em `alternativas.valueChanges` (ver `createPerguntaGroup`): lida a cada passada
   * do change detection, podia mudar entre a checagem e a verificação e disparar NG0100.
   */
  /**
   * Piso e teto que o participante pode somar respondendo o questionário. É a referência que as
   * faixas dos perfis precisam cobrir; fica `null` enquanto alguma pergunta estiver sem pontos.
   */
  readonly pontuacaoAtingivel = computed(() => {
    this.formRevisao();

    return calcularPontuacaoAtingivel(
      this.perguntaGroups.map((grupo) => ({
        alternativas: this.getAlternativaGroups(grupo).map((alternativa) => ({
          pontos: alternativa.get('pontos')?.value as number | null,
        })),
      })),
    );
  });

  /**
   * Problemas de cobertura das faixas — buracos, sobreposições ou extremos fora do atingível.
   * Um participante cuja pontuação caia num buraco termina o questionário sem perfil nenhum.
   * Vêm todos juntos: corrigir um de cada vez faria a pessoa salvar e voltar várias vezes.
   */
  readonly problemasCoberturaPerfis = computed<ProblemaCobertura[]>(() => {
    this.formRevisao();

    const atingivel = this.pontuacaoAtingivel();
    if (!atingivel || this.perfis.length === 0) {
      return [];
    }

    return validarCoberturaDePerfis(
      this.perfilGroups.map((grupo) => ({
        idPerfilInvestimento: grupo.get('idPerfilInvestimento')?.value as number,
        nomePerfil: (grupo.get('nomePerfil')?.value as string) || 'sem nome',
        pontuacaoMinima: grupo.get('pontuacaoMinima')?.value as number | null,
        pontuacaoMaxima: grupo.get('pontuacaoMaxima')?.value as number | null,
      })),
      atingivel,
    );
  });

  /** Campos citados por algum problema, na forma `{id}:{campo}`, para o destaque no template. */
  private readonly camposComProblema = computed(
    () =>
      new Set(
        this.problemasCoberturaPerfis().flatMap((problema) =>
          problema.campos.map((campo) => `${campo.idPerfilInvestimento}:${campo.campo}`),
        ),
      ),
  );

  /** Borda vermelha no campo que a mensagem manda ajustar. */
  pontuacaoComProblema(perfilGroup: FormGroup, campo: 'minima' | 'maxima'): boolean {
    const id = perfilGroup.get('idPerfilInvestimento')?.value as number;
    return this.camposComProblema().has(`${id}:${campo}`);
  }

  private readonly perguntasIncompletas = computed(() => {
    this.formRevisao();

    return new Set(
      this.perguntaGroups.flatMap((grupo, index) => (grupo.invalid ? [index] : [])),
    );
  });

  perguntaIncompleta(index: number): boolean {
    return this.perguntasIncompletas().has(index);
  }

  getAlternativas(perguntaGroup: FormGroup): FormArray {
    return perguntaGroup.get('alternativas') as FormArray;
  }

  temPerguntaComAlternativasInvalidas(): boolean {
    return this.perguntas.controls.some(
      (pergunta) => (pergunta.get('alternativas') as FormArray).invalid,
    );
  }

  getAlternativaGroups(perguntaGroup: FormGroup): FormGroup[] {
    return this.getAlternativas(perguntaGroup).controls as FormGroup[];
  }

  /**
   * Motivo pelo qual o questionário ainda não pode ser salvo, ou `null` quando está completo.
   * Desabilita os dois botões de salvar e é exibido acima deles — botão apagado sem explicação
   * deixa a pessoa sem saber o que falta. Mesmo padrão de `motivoIncluirDesabilitado` no modal
   * de pergunta.
   *
   * Precisa ser `computed` e não um getter: a validade das perguntas é atualizada por uma
   * subscription em `alternativas.valueChanges` (ver `createPerguntaGroup`), e um getter
   * reavaliado a cada passada do change detection podia devolver valores diferentes na mesma
   * checagem, disparando NG0100. O `computed` fica estável até `formRevisao` mudar.
   */
  readonly motivoSalvarDesabilitado = computed<string | null>(() => {
    this.formRevisao();

    if (this.form.get('nome')?.invalid) {
      return 'Informe o nome do questionário.';
    }

    if (this.form.get('tipo')?.invalid) {
      return 'Selecione o tipo de questionário.';
    }

    if (this.perguntas.length === 0) {
      return 'Adicione pelo menos uma pergunta.';
    }

    const perguntaIncompleta = this.perguntaGroups.findIndex((grupo) => grupo.invalid);
    if (perguntaIncompleta >= 0) {
      return `Complete a pergunta ${perguntaIncompleta + 1}: enunciado e alternativas.`;
    }

    if (this.perfis.length === 0) {
      return 'Associe pelo menos um perfil.';
    }

    const perfilIncompleto = this.perfilGroups.find((grupo) => grupo.invalid);
    if (perfilIncompleto) {
      const nome = perfilIncompleto.get('nomePerfil')?.value || 'associado';
      return `Informe a pontuação mínima e máxima do perfil ${nome}.`;
    }

    // A área do rodapé é de uma linha; a lista inteira fica na seção 3, ao lado das faixas.
    const [primeiroProblemaCobertura] = this.problemasCoberturaPerfis();
    if (primeiroProblemaCobertura) {
      return primeiroProblemaCobertura.mensagem;
    }

    // Rede final: qualquer obrigatório que não esteja enumerado acima ainda bloqueia o
    // salvamento, só sem uma mensagem específica.
    if (this.form.invalid) {
      return 'Preencha os campos obrigatórios.';
    }

    return null;
  });

  ngOnInit(): void {
    this.carregarPerfis();

    const id = this.questionarioId();
    if (id != null) {
      this.loadForEdit(id);
    }
  }

  /**
   * Carrega os perfis de investimento ativos apenas como opções do dropdown
   * "Lista perfil cadastrado" (`perfisDisponiveis`). Não popula mais o FormArray
   * `perfis` automaticamente — em modo edição, encadeia o carregamento das
   * pontuações já salvas para reconstruir esse FormArray a partir do que está
   * persistido. `carregandoPerfis` só vira `false` depois desse encadeamento
   * terminar (ver `carregarPontuacoes`): desligar o loading antes disso deixava
   * a UI mostrar "Nenhum perfil associado" por um instante e, sob change
   * detection zoneless, podia fazer o `perfis.length` do template mudar entre a
   * checagem normal e a de verificação do Angular, disparando NG0100.
   */
  private carregarPerfis(): void {
    this.carregandoPerfis.set(true);
    this.perfilService.getPerfis({ status: 'Ativo' }).subscribe({
      next: (perfis) => {
        this._perfisDisponiveis.set(perfis);

        const id = this.questionarioId();
        if (id != null) {
          this.carregarPontuacoes(id);
        } else {
          this.carregandoPerfis.set(false);
        }
      },
      error: () => {
        this.carregandoPerfis.set(false);
        this.toastService.show('Não foi possível carregar os perfis de investimento.', 'error');
      },
    });
  }

  /**
   * Reconstrói o FormArray `perfis` a partir das pontuações já persistidas do
   * questionário, um `createPerfilGroup` por pontuação, resolvendo o nome do
   * perfil em `perfisDisponiveis()`.
   */
  private carregarPontuacoes(idQuestionario: number): void {
    this.questionarioService.listarPontuacoes(idQuestionario).subscribe({
      next: (pontuacoes) => {
        this.replaceFormArray(
          this.perfis,
          pontuacoes.map((pontuacao) => {
            const perfil = this._perfisDisponiveis().find(
              (p) => p.id === pontuacao.idPerfilInvestimento,
            );
            return this.createPerfilGroup({
              idPerfilInvestimento: pontuacao.idPerfilInvestimento,
              nomePerfil: perfil ? perfil.nomeExibicao || perfil.nome : '',
              pontuacaoMinima: pontuacao.pontuacaoMinima,
              pontuacaoMaxima: pontuacao.pontuacaoMaxima,
            });
          }),
        );
        // As pontuações podem chegar depois do bloqueio, e grupos novos entram habilitados.
        if (this.edicaoBloqueada()) {
          this.perfis.disable();
        }
        this.pontuacoesPersistidas.clear();
        pontuacoes.forEach((pontuacao) =>
          this.pontuacoesPersistidas.add(pontuacao.idPerfilInvestimento),
        );
        this.carregandoPerfis.set(false);
      },
      error: () => {
        this.carregandoPerfis.set(false);
        this.toastService.show('Não foi possível carregar as pontuações do questionário.', 'error');
      },
    });
  }

  private loadForEdit(id: number): void {
    this.questionarioService.obterQuestionarioParaEdicao(id).subscribe({
      next: (formValue) => {
        if (!formValue) {
          this.toastService.show('Questionário não encontrado.', 'error');
          void this.router.navigate(['/questionarios']);
          return;
        }
        this.patchFormFromValue(formValue);

        if (formValue.permiteEdicao === false) {
          this.bloquearEdicao(formValue.motivoBloqueioEdicao ?? '');
        }
      },
      error: (error: unknown) => {
        const message =
          error instanceof ApiHttpError
            ? error.message
            : 'Não foi possível carregar o questionário. Tente novamente.';
        this.toastService.show(message, 'error');
        void this.router.navigate(['/questionarios']);
      },
    });
  }

  /**
   * Reconstrói o form completo a partir do valor carregado: além do nome, repopula
   * o FormArray de perguntas (com suas alternativas). Os `id`s são preservados nos
   * grupos para o sync de update/exclusão na gravação distinguir itens existentes de
   * novos. A seção de perfis é carregada à parte por `carregarPerfis`/`carregarPontuacoes`,
   * pois depende do cadastro de perfis (PerfilService), não do questionário.
   */
  private patchFormFromValue(formValue: QuestionarioFormValue): void {
    this.statusCarregado.set(formValue.status ?? null);
    this.replaceFormArray(
      this.perguntas,
      formValue.perguntas.map((pergunta) => this.createPerguntaGroup(pergunta)),
    );
    this.form.patchValue({
      nome: formValue.nome,
      tipo: formValue.tipo ?? TipoQuestionario.Suitability,
    });
  }

  /** Desabilita o formulário; ações que não passam por FormControl leem `edicaoBloqueada` no template. */
  private bloquearEdicao(motivo: string): void {
    this.edicaoBloqueada.set(true);
    this.motivoBloqueioEdicao.set(motivo || MOTIVO_BLOQUEIO_EDICAO_PADRAO);
    this.form.disable();
    this.perfilSelecionadoId.disable();
  }

  private replaceFormArray(target: FormArray, groups: FormGroup[]): void {
    if (target === this.perguntas) {
      target.controls.forEach((group) => this.unsubscribePerguntaAlternativas(group as FormGroup));
    }
    target.clear();
    groups.forEach((group) => target.push(group));
  }

  private unsubscribePerguntaAlternativas(group: FormGroup): void {
    this.perguntaAlternativasSubscriptions.get(group)?.unsubscribe();
    this.perguntaAlternativasSubscriptions.delete(group);
  }

  private createPerguntaGroup(pergunta?: QuestionarioPergunta): FormGroup {
    const alternativas =
      pergunta?.alternativas && pergunta.alternativas.length > 0
        ? pergunta.alternativas.map((alternativa) => this.createAlternativaGroup(alternativa))
        : [this.createAlternativaGroup(), this.createAlternativaGroup()];

    const perguntaGroup = this.fb.group({
      id: [pergunta?.id ?? null],
      texto: [pergunta?.texto ?? '', Validators.required],
      tipo: [pergunta?.tipo ?? TipoPergunta.Alternativa],
      // Removido por decisão do PO — peso removido, ver plano
      // 2026-08-11-questionario-erro-exclusao-e-remove-peso.
      // peso: [pergunta?.peso ?? 1, [Validators.required, Validators.min(0)]],
      alternativas: this.fb.array(alternativas, Validators.minLength(2)),
    });

    // O validador vive no grupo pai; alterações nas alternativas (pontos,
    // adição/remoção) não disparam a revalidação do grupo sozinhas.
    const subscription = perguntaGroup
      .get('alternativas')!
      .valueChanges.subscribe(() => perguntaGroup.updateValueAndValidity());

    // Removido por decisão do PO — peso removido, ver plano
    // 2026-08-11-questionario-erro-exclusao-e-remove-peso.
    // pontosNaoExcedePesoValidator depende do peso; se o peso mudar depois que
    // as alternativas já tiverem pontos preenchidos, precisa revalidá-las.
    // subscription.add(
    //   perguntaGroup.get('peso')!.valueChanges.subscribe(() => {
    //     (perguntaGroup.get('alternativas') as FormArray).controls.forEach((alternativaGroup) =>
    //       alternativaGroup.get('pontos')?.updateValueAndValidity({ onlySelf: true }),
    //     );
    //   }),
    // );
    this.perguntaAlternativasSubscriptions.set(perguntaGroup, subscription);

    return perguntaGroup;
  }

  private createAlternativaGroup(alternativa?: QuestionarioAlternativa): FormGroup {
    return this.fb.group({
      id: [alternativa?.id ?? null],
      texto: [alternativa?.texto ?? '', Validators.required],
      pontos: [
        alternativa?.pontos ?? null,
        [Validators.required, Validators.min(0), Validators.max(9999)],
      ],
    });
  }

  private createPerfilGroup(perfil: QuestionarioPerfil): FormGroup {
    return this.fb.group({
      idPerfilInvestimento: [perfil.idPerfilInvestimento],
      nomePerfil: [perfil.nomePerfil],
      pontuacaoMinima: [perfil.pontuacaoMinima, [Validators.required, Validators.min(0)]],
      pontuacaoMaxima: [perfil.pontuacaoMaxima, [Validators.required, Validators.min(0)]],
    });
  }

  onAdicionarPerfilAssociado(): void {
    const idSelecionado = this.perfilSelecionadoId.value;
    if (idSelecionado == null) {
      return;
    }
    const jaAssociado = this.perfis.controls.some(
      (grupo) => grupo.get('idPerfilInvestimento')?.value === idSelecionado,
    );
    if (jaAssociado) {
      return;
    }

    const perfil = this.perfisDisponiveis().find((p) => p.id === idSelecionado);
    this.perfis.push(
      this.createPerfilGroup({
        idPerfilInvestimento: idSelecionado,
        nomePerfil: perfil ? perfil.nomeExibicao || perfil.nome : '',
        pontuacaoMinima: null,
        pontuacaoMaxima: null,
      }),
    );
    this.perfilSelecionadoId.reset(null);
  }

  onExcluirPerfilAssociado(index: number): void {
    this.perfilParaExcluir.set(index);
  }

  onCancelarExclusaoPerfil(): void {
    this.perfilParaExcluir.set(null);
  }

  /**
   * A lixeira do perfil exclui a pontuação na hora, não mais no salvamento: confirmado o
   * modal, o DELETE sai e a linha só some da tela quando a API responde.
   */
  onConfirmarExclusaoPerfil(): void {
    const index = this.perfilParaExcluir();
    if (index == null) {
      return;
    }

    this.perfilParaExcluir.set(null);

    const idPerfilInvestimento = this.perfis.at(index).get('idPerfilInvestimento')
      ?.value as number | null;
    const idQuestionario = this.questionarioId();

    if (
      idPerfilInvestimento == null ||
      idQuestionario == null ||
      !this.pontuacoesPersistidas.has(idPerfilInvestimento)
    ) {
      this.perfis.removeAt(index);
      return;
    }

    this.excluindo.set(true);
    this.questionarioService
      .excluirPontuacaoPerfil(idPerfilInvestimento, idQuestionario)
      .subscribe({
        next: () => {
          this.excluindo.set(false);
          this.pontuacoesPersistidas.delete(idPerfilInvestimento);
          this.perfis.removeAt(index);
          this.toastService.show('Perfil removido do questionário.', 'success');
        },
        error: (error: unknown) => {
          this.excluindo.set(false);
          this.toastService.show(
            this.resolveExclusaoErrorMessage(error, 'o perfil'),
            'error',
          );
        },
      });
  }

  onAdicionarPergunta(): void {
    this.perguntas.push(this.createPerguntaGroup());
  }

  onRemoverPergunta(index: number): void {
    const group = this.perguntas.at(index) as FormGroup;
    this.unsubscribePerguntaAlternativas(group);
    this.perguntas.removeAt(index);
  }

  ngOnDestroy(): void {
    this.perguntaAlternativasSubscriptions.forEach((subscription) => subscription.unsubscribe());
    this.perguntaAlternativasSubscriptions.clear();
  }

  onVisualizarPergunta(index: number): void {
    this.perguntaSelecionada.set({ index, modo: 'visualizar' });
  }

  onEditarPergunta(index: number): void {
    this.perguntaSelecionada.set({ index, modo: 'editar' });
  }

  onExcluirPergunta(index: number): void {
    this.perguntaParaExcluir.set(index);
  }

  onCancelarExclusaoPergunta(): void {
    this.perguntaParaExcluir.set(null);
  }

  /**
   * A lixeira da pergunta exclui na API assim que o modal é confirmado — o salvamento não
   * carrega mais exclusões pendentes. Pergunta ainda não salva existe só no formulário e sai
   * sem chamada.
   */
  onConfirmarExclusaoPergunta(): void {
    const index = this.perguntaParaExcluir();
    if (index == null) {
      return;
    }

    this.perguntaParaExcluir.set(null);

    const perguntaGroup = this.perguntas.at(index) as FormGroup;
    const idPergunta = perguntaGroup.get('id')?.value as number | null;

    if (idPergunta == null) {
      this.onRemoverPergunta(index);
      return;
    }

    const idsAlternativas = this.getAlternativaGroups(perguntaGroup)
      .map((alternativaGroup) => alternativaGroup.get('id')?.value as number | null)
      .filter((id): id is number => id != null);

    this.excluindo.set(true);
    this.questionarioService.excluirPergunta(idPergunta, idsAlternativas).subscribe({
      next: () => {
        this.excluindo.set(false);
        this.onRemoverPergunta(index);
        this.toastService.show('Pergunta excluída com sucesso.', 'success');
      },
      error: (error: unknown) => {
        this.excluindo.set(false);
        this.toastService.show(
          this.resolveExclusaoErrorMessage(error, 'a pergunta'),
          'error',
        );
      },
    });
  }

  onAbrirCriarPergunta(): void {
    this.criarPerguntaSubmitAttempted.set(false);
    this.criarPerguntaForm.reset({ texto: '' });
    this.showCriarPerguntaModal.set(true);
  }

  onCancelarCriarPergunta(): void {
    this.showCriarPerguntaModal.set(false);
  }

  onConfirmarCriarPergunta(): void {
    this.criarPerguntaSubmitAttempted.set(true);
    if (this.criarPerguntaForm.invalid) {
      return;
    }

    this.onAdicionarPergunta();
    this.perguntas.at(this.perguntas.length - 1).patchValue({
      texto: this.criarPerguntaForm.value.texto,
    });
    this.showCriarPerguntaModal.set(false);
  }

  onAbrirSelecionarPergunta(): void {
    this.showSelecionarPerguntaModal.set(true);
    this.carregandoPerguntasCadastradas.set(true);
    this.questionarioService.listarPerguntasCadastradas().subscribe({
      next: (perguntas) => {
        this.perguntasCadastradas.set(perguntas);
        this.carregandoPerguntasCadastradas.set(false);
      },
      error: () => {
        this.carregandoPerguntasCadastradas.set(false);
        this.toastService.show('Não foi possível carregar as perguntas cadastradas.', 'error');
      },
    });
  }

  onCancelarSelecionarPergunta(): void {
    this.showSelecionarPerguntaModal.set(false);
  }

  onAbrirVisualizarQuestionario(): void {
    this.visualizarQuestionarioValue.set(this.form.getRawValue() as QuestionarioFormValue);
    this.showVisualizarModal.set(true);
  }

  onFecharVisualizarQuestionario(): void {
    this.showVisualizarModal.set(false);
  }

  /**
   * "Exportar" vai direto para a janela de impressão do navegador, sem abrir o modal de
   * visualização na frente do usuário.
   *
   * O PDF é montado a partir do snapshot do formulário, não do DOM: `questionario-pdf` gera o
   * documento direto do `QuestionarioFormValue`, então nenhum elemento de interface (cabeçalho,
   * menu, botões, rodapé, modal) tem como entrar no arquivo. O download começa sozinho, sem
   * passar pela janela de impressão do navegador.
   */
  onExportarQuestionario(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const questionario = this.form.getRawValue() as QuestionarioFormValue;

    try {
      this.baixarArquivo(
        createQuestionarioPdfBlob(questionario),
        createQuestionarioPdfFileName(questionario),
      );
    } catch {
      this.toastService.show('Não foi possível gerar o PDF do questionário.', 'error');
    }
  }

  /** Dispara o download de um Blob via link temporário. */
  private baixarArquivo(blob: Blob, nomeArquivo: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = nomeArquivo;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Libera a memória do Blob; o download já foi iniciado pelo clique acima.
    URL.revokeObjectURL(url);
  }

  onSelecionarPerguntaCadastrada(pergunta: QuestionarioPergunta): void {
    this.questionarioService.obterAlternativasParaReaproveitar(pergunta.id!).subscribe({
      next: (alternativas) => {
        this.perguntas.push(
          this.createPerguntaGroup({
            texto: pergunta.texto,
            tipo: pergunta.tipo,
            alternativas,
          }),
        );
        this.showSelecionarPerguntaModal.set(false);
      },
      error: () => {
        this.toastService.show('Não foi possível reaproveitar a pergunta.', 'error');
      },
    });
  }

  onPerguntaDragStart(index: number): void {
    this.perguntaArrastada.set(index);
  }

  onPerguntaDragOver(event: DragEvent): void {
    event.preventDefault();
  }

  onPerguntaDrop(targetIndex: number): void {
    const origem = this.perguntaArrastada();
    this.perguntaArrastada.set(null);
    if (origem == null || origem === targetIndex) {
      return;
    }

    const control = this.perguntas.at(origem);
    this.perguntas.removeAt(origem);
    this.perguntas.insert(targetIndex, control);
  }

  onAdicionarAlternativa(perguntaGroup: FormGroup): void {
    this.getAlternativas(perguntaGroup).push(this.createAlternativaGroup());
  }

  onExcluirAlternativa(perguntaGroup: FormGroup, index: number): void {
    // O botão já fica desabilitado no mínimo de 2, mas a regra também vale aqui.
    if (this.getAlternativas(perguntaGroup).length <= 2) {
      return;
    }

    this.alternativaParaExcluir.set({ perguntaGroup, index });
  }

  onCancelarExclusaoAlternativa(): void {
    this.alternativaParaExcluir.set(null);
  }

  /**
   * Mesma regra das lixeiras de pergunta e perfil: alternativa já persistida sai da API na
   * hora, porque o salvamento não exclui mais nada.
   */
  onConfirmarExclusaoAlternativa(): void {
    const selecionada = this.alternativaParaExcluir();
    if (selecionada == null) {
      return;
    }

    this.alternativaParaExcluir.set(null);

    const alternativas = this.getAlternativas(selecionada.perguntaGroup);
    const idAlternativa = alternativas.at(selecionada.index).get('id')?.value as number | null;

    if (idAlternativa == null) {
      alternativas.removeAt(selecionada.index);
      return;
    }

    this.excluindo.set(true);
    this.questionarioService.excluirAlternativa(idAlternativa).subscribe({
      next: () => {
        this.excluindo.set(false);
        alternativas.removeAt(selecionada.index);
        this.toastService.show('Alternativa excluída com sucesso.', 'success');
      },
      error: (error: unknown) => {
        this.excluindo.set(false);
        this.toastService.show(
          this.resolveExclusaoErrorMessage(error, 'a alternativa'),
          'error',
        );
      },
    });
  }

  showControlError(controlName: string): boolean {
    const control = this.form.controls[controlName];
    return control.invalid && (this.submitAttempted() || control.touched);
  }

  showPerguntaControlError(perguntaGroup: FormGroup, controlName: string): boolean {
    const control = perguntaGroup.get(controlName);
    if (!control) return false;
    return control.invalid && (this.submitAttempted() || control.touched);
  }

  showPerfilControlError(perfilGroup: FormGroup, controlName: string): boolean {
    const control = perfilGroup.get(controlName);
    if (!control) return false;
    return control.invalid && (this.submitAttempted() || control.touched);
  }

  onCancelar(): void {
    void this.router.navigate(['/questionarios']);
  }

  onSalvarRascunho(): void {
    if (this.edicaoBloqueada()) {
      return;
    }

    const nome = (this.form.controls['nome'].value as string).trim();
    if (!nome) {
      this.form.controls['nome'].markAsTouched();
      this.toastService.show(
        'Informe o nome do questionário para salvar o rascunho.',
        'error',
      );
      return;
    }

    if (this.temPerguntaComAlternativasInvalidas()) {
      this.submitAttempted.set(true);
      this.form.markAllAsTouched();
      this.toastService.show(
        'Verifique as alternativas: pelo menos 2 por pergunta, pontos entre 0 e 9999.',
        'error',
      );
      return;
    }

    if (!this.isEditMode() && !this.validarCriacao()) {
      return;
    }

    this.isSaving.set(true);
    this.gravar(QUESTIONARIO_STATUS_RASCUNHO).subscribe({
      next: (saved) => {
        this.isSaving.set(false);
        this.toastService.show('Rascunho salvo com sucesso.', 'success');

        // As pontuações preenchidas acabaram de ser gravadas: a lixeira dessas linhas passa a
        // precisar do DELETE.
        this.registrarPontuacoesPersistidas();

        const idEmEdicao = this.questionarioId();
        if (idEmEdicao != null) {
          // Já estamos na rota de edição, e `navigate` para a mesma URL é ignorado pelo router:
          // sem recarregar, as perguntas e alternativas recém-criadas continuariam com `id`
          // nulo no formulário. No salvamento seguinte elas seriam tratadas como novas —
          // recriadas por POST e as originais excluídas junto com as demais.
          this.loadForEdit(idEmEdicao);
          return;
        }

        void this.router.navigate(['/questionarios', saved.id, 'editar']);
      },
      error: (error: unknown) => {
        this.isSaving.set(false);
        this.toastService.show(this.resolveErrorMessage(error), 'error');
      },
    });
  }

  onSalvar(): void {
    if (this.edicaoBloqueada()) {
      return;
    }

    this.submitAttempted.set(true);
    this.form.markAllAsTouched();

    // Na criação a checagem específica vem antes: "Associe pelo menos um perfil" ajuda mais
    // do que o "Preencha os campos obrigatórios" genérico do form.
    if (!this.isEditMode() && !this.validarCriacao()) {
      this.scrollToFirstInvalidField();
      return;
    }

    if (this.form.invalid) {
      this.toastService.show(
        'Preencha os campos obrigatórios antes de salvar.',
        'error',
      );
      this.scrollToFirstInvalidField();
      return;
    }

    this.isSaving.set(true);
    this.gravar(QUESTIONARIO_STATUS_PUBLICADO).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.toastService.show('Questionário salvo com sucesso.', 'success');
        void this.router.navigate(['/questionarios']);
      },
      error: (error: unknown) => {
        this.isSaving.set(false);
        this.toastService.show(this.resolveErrorMessage(error), 'error');
      },
    });
  }

  /**
   * Criação manda tudo em um único `POST /v1/Questionario`; edição segue no fluxo incremental,
   * que sincroniza perguntas, alternativas e pontuações uma a uma sobre o que já existe.
   */
  private gravar(status: StatusQuestionarioApi): Observable<Questionario> {
    const payload = this.buildPayload();

    if (!this.isEditMode()) {
      return this.questionarioService.criarQuestionario(payload, status);
    }

    return status === QUESTIONARIO_STATUS_RASCUNHO
      ? this.questionarioService.salvarRascunho(payload)
      : this.questionarioService.publicarQuestionario(payload);
  }

  /**
   * Rede de segurança antes do POST, com a mesma regra que desabilita os botões: eles já
   * impedem o clique, mas o endpoint de criação recebe a estrutura inteira e não convém
   * depender só do estado da tela para não gravar um questionário incompleto.
   */
  private validarCriacao(): boolean {
    const motivo = this.motivoSalvarDesabilitado();
    if (motivo === null) {
      return true;
    }

    this.submitAttempted.set(true);
    this.form.markAllAsTouched();
    this.toastService.show(motivo, 'error');

    return false;
  }

  private buildPayload(): QuestionarioFormValue {
    const raw = this.form.getRawValue() as QuestionarioFormValue;
    const id = this.questionarioId();
    return id != null ? { ...raw, id } : raw;
  }

  private registrarPontuacoesPersistidas(): void {
    this.perfilGroups
      .filter(
        (grupo) =>
          grupo.get('pontuacaoMinima')?.value != null ||
          grupo.get('pontuacaoMaxima')?.value != null,
      )
      .forEach((grupo) =>
        this.pontuacoesPersistidas.add(grupo.get('idPerfilInvestimento')?.value as number),
      );
  }

  private resolveExclusaoErrorMessage(error: unknown, recurso: string): string {
    if (error instanceof ApiHttpError) {
      return error.message;
    }

    return `Não foi possível excluir ${recurso}. Tente novamente.`;
  }

  private resolveErrorMessage(error: unknown): string {
    if (error instanceof QuestionarioSaveError) {
      const originalMessage = this.resolveOriginalErrorMessage(error.originalError);
      if (error.rollbackComplete) {
        return `${originalMessage} Os valores anteriores foram restaurados e os registros parciais removidos.`;
      }

      return `${originalMessage} A limpeza ficou incompleta e o estado pode estar parcialmente salvo. Verifique antes de tentar novamente.`;
    }

    return this.resolveOriginalErrorMessage(error);
  }

  private resolveOriginalErrorMessage(error: unknown): string {
    if (error instanceof ApiHttpError) {
      return error.message;
    }
    return 'Não foi possível salvar o questionário. Tente novamente.';
  }

  private scrollToFirstInvalidField(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const formElement = document.querySelector('.campanha-form');
    if (!formElement) return;
    const firstInvalid = formElement.querySelector('.is-invalid, [data-testid$="-error"]');
    firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}
